import { ReactNode } from 'react';

import { XIcon } from '@heroicons/react/outline';

import { CsMemoPanelBase } from './CsMemoPanelBase';
import { CsMemoAdapter, CsMemoRecord } from './csMemoAdapter';

export interface CsMemoModalBaseProps<TTarget extends object> {
  adapter: CsMemoAdapter<TTarget>;
  title: string;
  subtitle?: string;
  target: TTarget;
  memos?: CsMemoRecord[];
  isLoading?: boolean;
  unavailable?: boolean;
  subLabelMap?: Map<string, string>;
  /** 타임라인 위에 띄울 안내 문구 */
  notice?: string;
  /** 헤더 오른쪽에 끼워 넣을 것 (화환 배송완료 사진 등 — 응대하며 같이 보는 값) */
  headerExtra?: ReactNode;
  onClose: () => void;
  /** 상세 모달(z-50)·확인창(z-[60]) 위에 뜨도록 기본 z-[70] */
  zIndexClassName?: string;
}

/** CS 응대 메모장 — 어디서든 같은 모양으로 띄운다 (답례품·화환 공용) */
export function CsMemoModalBase<TTarget extends object>({
  adapter,
  title,
  subtitle,
  target,
  memos,
  isLoading,
  unavailable,
  subLabelMap,
  notice,
  headerExtra,
  onClose,
  zIndexClassName = 'z-[70]',
}: CsMemoModalBaseProps<TTarget>) {
  return (
    <div
      className={`fixed inset-0 ${zIndexClassName} flex items-center justify-center p-4`}
    >
      <div className="fixed inset-0 bg-black/40" onClick={onClose} />
      <div className="relative flex max-h-[85vh] w-full max-w-lg flex-col overflow-hidden rounded-xl bg-white shadow-2xl">
        <div className="flex items-start justify-between gap-3 px-5 py-3">
          <div className="min-w-0">
            <h3 className="truncate text-sm font-bold text-gray-900">{title}</h3>
            {subtitle && (
              <p className="mt-0.5 truncate text-xs text-gray-500">{subtitle}</p>
            )}
          </div>
          <div className="flex shrink-0 items-start gap-2">
            {headerExtra}
            <button
              onClick={onClose}
              className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            >
              <XIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="flex min-h-0 flex-col gap-2 px-5 pb-5">
          {notice && (
            <p className="rounded-lg bg-indigo-50 px-3 py-2 text-[11px] leading-relaxed text-indigo-700">
              {notice}
            </p>
          )}
          <CsMemoPanelBase
            adapter={adapter}
            target={target}
            memos={memos}
            isLoading={isLoading}
            unavailable={unavailable}
            subLabelMap={subLabelMap}
            listMaxHeightClassName="max-h-[45vh]"
            autoFocus
          />
        </div>
      </div>
    </div>
  );
}
